<script setup>

import { inject, ref, computed } from 'vue';
import { normerNom, TypeNommage } from '@/scripts/global.js';
import { 
    getErreurValeurListeRequise,
    getErreurNomNorme } from '@/scripts/controlesSaisie.js';
import Dialog from 'primevue/dialog';
import MultiSelect from 'primevue/multiselect';
import Formulaire from '@/components/generiques/Formulaire.vue';
import SaisieListe from '@/components/generiques/SaisieListe.vue';
import SaisieAutocompletion from '@/components/generiques/SaisieAutocompletion.vue';

const emit = defineEmits(['alimentationChamps', 'fermeture']);

const { colonnesSelectionnees, variables } = defineProps({
    colonnesSelectionnees: Array,
    variables: Array
});

const typesObjetUniques = inject('typesObjetUniques');
const getTypeObjet = inject('getTypeObjet');
const getDimension = inject('getDimension');
const dimensions = inject('dimensions');

const alimentationChamps = ref({
    objet: null,
    champParColonne: initialiserChampParColonne(),
    dimensions: []
});
const suggestionsChamps = ref([]);

const objetsProposes = computed(() => {
    const objets = [];
    const objetsVariables = variables.filter(variable => variable.typeDonnee?.classe === 'OBJET').map(variable => {
        return {nom: variable.nom, idTypeObjet: variable.typeDonnee.id, type: 'variable'};
    });
    if (objetsVariables.length > 0) {
        objets.push({groupe: 'Variables d\'objet', objets: objetsVariables});
    }
    const objetsUniques = typesObjetUniques.value.map(typeObjet => {
        return {nom: typeObjet.nom, idTypeObjet: typeObjet.id, type: 'typeObjet'};
    });
    if (objetsUniques.length > 0) {
        objets.push({groupe: 'Objets uniques', objets: objetsUniques});
    }
    return objets;
});

const typeObjet = computed(() => {
    const objet = alimentationChamps.value.objet;
    if (objet != null) {
        return getTypeObjet(objet.idTypeObjet);
    }
});

const dimensionsProposees = computed(() => {
    return variables.filter(variable => dimensions.value.some(dim => dim.nom === variable.nom));
});

const nomsDimensionsSelectionnees = computed(() => {
    return alimentationChamps.value.dimensions.map(dim => dim.nom);
});

const idDimensionsSelectionnees = computed(() => {
    return dimensions.value.filter(dim => nomsDimensionsSelectionnees.value.includes(dim.nom)).map(dim => dim.id);
});

function initialiserChampParColonne() {
    const champParColonne = {};
    for (const colonne of colonnesSelectionnees) {
        champParColonne[colonne.nom] = normerNom(colonne.nom.toLowerCase(), TypeNommage.VARIABLE);
    }
    return champParColonne;
}

function rafraichirSuggestionsChamps(texteSaisi) {
    if (typeObjet.value != null) {
        suggestionsChamps.value = typeObjet.value.champs.map(chp => chp.nom).filter(nomChamp => nomChamp.toLowerCase().includes(texteSaisi));
    } else {
        suggestionsChamps.value = [];
    }
}

function estNouveauChamp(nomChamp) {
    if (!nomChamp) {
        return false;
    }
    if (!typeObjet.value) {
        return false;
    }
    return !typeObjet.value.champs.some(champ => champ.nom === nomChamp);
}

function validerSaisie() {
    const erreurs = {};
    erreurs.objet = getErreurValeurListeRequise(alimentationChamps.value.objet);
    for (const [nomColonne, nomChamp] of Object.entries(alimentationChamps.value.champParColonne)) {
        let idErreur = `colonne_${nomColonne}`;
        erreurs[idErreur] = getErreurNomNorme(nomChamp, TypeNommage.VARIABLE);
        if (typeObjet.value && !erreurs[idErreur] && !estNouveauChamp(nomChamp)) {
            let champ = typeObjet.value.champs.find(champ => champ.nom === nomChamp);
            let idDimensionsManquantes = champ.dimensions.filter(idDimension => !idDimensionsSelectionnees.value.includes(idDimension));
            if (idDimensionsManquantes.length) {
                erreurs[idErreur] = `Les dimensions suivantes du champ n'ont pas été sélectionnées : ${idDimensionsManquantes.map(idDim => getDimension(idDim).nom).join(', ')}`;
            } else {
                let idDimensionsSuperflues = idDimensionsSelectionnees.value.filter(idDimension => !champ.dimensions.includes(idDimension));
                if (idDimensionsSuperflues.length) {
                    erreurs[idErreur] = `Le champ n'est pas sensible aux dimensions suivantes : ${idDimensionsSuperflues.map(idDim => getDimension(idDim).nom).join(', ')}`;
                }
            }
        }
    }
    return erreurs;
}

function validerFormulaire() {
    alimentationChamps.value.validationFormulaire = true;
}

function alimenterChamps() {
    const infosAlimentationChamps = {
        exprObjet: alimentationChamps.value.objet.nom,
        typeExprObjet: alimentationChamps.value.objet.type,
        nomsDimensions: alimentationChamps.value.dimensions.map(dim => dim.id),
        champsAlimentes: Object.entries(alimentationChamps.value.champParColonne).map(([cle, valeur]) => ({nomColonne: cle, nomChamp: valeur}))
    };
    emit('alimentationChamps', infosAlimentationChamps);
}

function gererFermeture(visible) {
    if (!visible) {
        emit('fermeture');
    }
}

</script>

<template>
    <Dialog 
        :visible="true" 
        modal 
        :header="'Alimentation des valeurs d\'un objet à partir de colonnes du fichier'"
        @update:visible="gererFermeture">
            <Formulaire
                :objet="alimentationChamps"
                :fonction-validation-saisie="validerSaisie"
                @validation="alimenterChamps">
                <label class="intitule titre">Objet :</label>
                <SaisieListe 
                    v-model:valeur="alimentationChamps.objet" 
                    :id-erreur="'objet'"
                    :options="objetsProposes"
                    option-label="nom"
                    option-group-label="groupe"
                    option-group-children="objets" />
                <template v-if="dimensionsProposees.length">
                    <label class="intitule titre">Dimensions des champs alimentés :</label>
                    <MultiSelect 
                        class="multiselect-dimensions"
                        v-model="alimentationChamps.dimensions" 
                        :options="dimensionsProposees"
                        option-label="nom"
                        display="chip"
                        :showToggleAll="false" />
                </template>
                <label class="intitule titre entete-corresp-colonne-champ">Colonne</label>
                <label class="intitule titre entete-corresp-colonne-champ">Champ</label>
                <template v-for="colonne in colonnesSelectionnees" :key="colonne.nom">
                    <label class="intitule">{{ colonne.nom }}</label>
                    <div class="zone-saisie-champ">
                        <SaisieAutocompletion 
                            class="nom-champ"
                            v-model:valeur="alimentationChamps.champParColonne[colonne.nom]" 
                            :id-erreur="`colonne_${colonne.nom}`" 
                            :suggestions="suggestionsChamps"
                            @modif-saisie="rafraichirSuggestionsChamps" />
                        <i class="icone-nouvel-element fas fa-circle-plus"
                            :class="{invisible: !estNouveauChamp(alimentationChamps.champParColonne[colonne.nom])}"
                            v-tooltip="'Nouveau champ'" />
                    </div>
                </template>
            </Formulaire>
            <template #footer>
                <button 
                    class="bouton fas fa-check" 
                    :disabled="false"
                    @click="validerFormulaire()"/>
            </template>
    </Dialog>
</template>

<style scoped>

.entete-corresp-colonne-champ {
    margin-top: 20px;
}

.zone-saisie-champ {
    display: flex;
    gap: 5px;
    align-items: center;
}

.nom-champ {
    max-height: 2rem;
    flex-grow: 1;
}

.icone-nouvel-element {
    color: green;
}

</style>